import { useEffect, useRef } from "react";
import { atom, useAtom } from "jotai";
import {
  trackDesignLabPricingChanged,
  type DesignLabPricingChangeReason
} from "@design-lab/shared/lib/analytics";
import { useCompositionPricing } from "./useCompositionPricing";

/**
 * Atom that holds the pending reason for the next pricing change.
 * Caller (qty/size/color action) sets this BEFORE mutating form state.
 * usePricingChangeTracker fires the event on the next price observation
 * and clears the atom.
 */
export const pricingChangeReasonAtom = atom<DesignLabPricingChangeReason | null>(null);

/**
 * Watches totalPriceWithBulkDiscount and emits design_lab_pricing_changed
 * when the price changes after a qty/size/color action set the reason atom.
 * Mount once near the DL root (alongside useDeliveryDateSync).
 */
export function usePricingChangeTracker(): void {
  const { totalPriceWithBulkDiscount } = useCompositionPricing();
  const [pendingReason, setPendingReason] = useAtom(pricingChangeReasonAtom);
  const prevPriceRef = useRef<number | null>(null);

  useEffect(() => {
    const prev = prevPriceRef.current;
    const curr = totalPriceWithBulkDiscount;

    if (pendingReason !== null) {
      if (prev !== null && prev !== curr) {
        trackDesignLabPricingChanged({ fromPrice: prev, toPrice: curr, reason: pendingReason });
      }
      // Clear the atom after one observation even if no price change happened.
      // Prevents a stale reason (e.g. "color_changed" with no price impact)
      // from inheriting onto a future unrelated price change.
      setPendingReason(null);
    }

    prevPriceRef.current = curr;
  }, [totalPriceWithBulkDiscount, pendingReason, setPendingReason]);
}
